import { useState } from 'react';
import { AIU_DEFINITION } from '../metricInfo';
import { Term } from './InfoTooltip';
import { ChartCard } from './ChartCard';
import { Modal } from './Modal';
import type { PremiumQuotaByModelPoint } from '@ace-tracker/shared';

const BAR_COLORS = ['#1DB8F2', '#7B2FBE', '#0058AB', '#00B4D8', '#06D6A0'];
const TOP_N = 5;

export function CostEfficiencyChart({
  byModel,
  currency,
  formatCurrency,
  info,
  className,
}: {
  byModel: PremiumQuotaByModelPoint[];
  currency: string;
  formatCurrency: (value: number, currency: string) => string;
  info?: string;
  className?: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const sorted = [...byModel].sort((a, b) => b.aiuPerCall - a.aiuPerCall);
  const max = Math.max(...sorted.map((m) => m.aiuPerCall), 0.01);
  const visible = sorted.slice(0, TOP_N);
  const hasMore = sorted.length > TOP_N;

  return (
    <>
      <ChartCard
        title="AIU per call by model"
        caption="Recorded AIU per call with the legacy AIU estimate — lower is lighter. Hover a bar for totals."
        info={info}
        height="h-96"
        className={className}
        actions={
          hasMore && (
            <button
              type="button"
              onClick={() => setIsExpanded(true)}
              aria-label={`Show all ${sorted.length} models`}
              className="flex h-11 w-11 items-center justify-center rounded-input text-text-muted hover:bg-surface-alt hover:text-text-default focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M9 3H3v6M15 21h6v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )
        }
      >
        <CostEfficiencyRows models={visible} max={max} currency={currency} formatCurrency={formatCurrency} compact />
        {hasMore && (
          <p className="mt-2 text-center text-[11px] text-text-faint">
            Showing top {TOP_N} of {sorted.length} models
          </p>
        )}
      </ChartCard>

      <Modal open={isExpanded} onClose={() => setIsExpanded(false)} title="AIU per call by model">
        <CostEfficiencyRows models={sorted} max={max} currency={currency} formatCurrency={formatCurrency} />
      </Modal>
    </>
  );
}

function CostEfficiencyRows({
  models,
  max,
  currency,
  formatCurrency,
  compact = false,
}: {
  models: PremiumQuotaByModelPoint[];
  max: number;
  currency: string;
  formatCurrency: (value: number, currency: string) => string;
  compact?: boolean;
}) {
  const axisOffset = compact ? '6.5rem' : '7.75rem'; // label column + gap — keep in sync with the row layout below

  return (
    <div className={`relative flex flex-col ${compact ? 'gap-1.5' : 'gap-1'}`}>
      <div className="pointer-events-none absolute bottom-0 top-0 w-px bg-border" style={{ left: axisOffset }} aria-hidden="true" />
      {models.map((m, i) => {
        const isZero = m.aiuPerCall <= 0;
        const widthPercent = isZero ? 0 : Math.max((m.aiuPerCall / max) * 100, 4);
        return (
          <div
            key={m.model}
            className={`group relative -mx-2 flex items-center rounded px-2 hover:bg-surface-alt ${
              compact ? 'gap-2 py-0.5' : 'gap-3 py-1'
            }`}
          >
            <span
              className={`shrink-0 truncate text-sm ${compact ? 'w-24' : 'w-28'} ${isZero ? 'text-text-faint' : 'text-text-default'}`}
            >
              {m.model}
            </span>
            <div className={compact ? 'h-5 flex-1' : 'h-6 flex-1'}>
              {isZero ? (
                <div className="mt-2 h-0.5 w-1.5 bg-text-faint" />
              ) : (
                <div
                  className="h-full rounded transition-[filter] duration-150 group-hover:brightness-110"
                  style={{ width: `${widthPercent}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }}
                />
              )}
            </div>
            <div className="flex w-24 shrink-0 flex-col items-end leading-tight">
              <span className={`text-sm font-bold tabular-nums ${isZero ? 'text-text-faint' : 'text-text-default'}`}>
                {m.aiuPerCall.toFixed(2)} <Term title={AIU_DEFINITION}>AIU</Term>
              </span>
              <span className="text-[11px] tabular-nums text-text-muted">{formatCurrency(m.indicativeCostPerCall, currency)}</span>
            </div>

            <span
              role="tooltip"
              className="pointer-events-none absolute right-0 top-full z-20 mt-1 w-max max-w-xs rounded-input border border-border bg-surface p-2 text-xs font-normal text-text-default opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
            >
              {`${m.calls} calls · ${m.aiu.toFixed(2)} AIU total · ${formatCurrency(m.indicativeCost, currency)} total`}
            </span>
          </div>
        );
      })}
      {models.length === 0 && <p className="text-sm text-text-muted">No premium quota usage in this range.</p>}
    </div>
  );
}
